import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Panel,
  PanelHeader,
  PanelContent,
  ProgressBar,
  TooltipTabstop,
  Icon
} from '@deque/cauldron-react';
import type { Manifest } from '../../common/api-client';
import styles from './TestDetails.css';
import {
  getBaseFilters,
  getSearchParamsFromFilters
} from '../../common/hooks/useIssuesFilter';
import type {
  UniversalIssueWithScreenshot,
  manifestUtils,
  Filters
} from '@deque/axe-devtools-issues-utils';

export const formatDateTime = (date: Date): string => {
  const dateObject = new Date(date);
  return dateObject.toLocaleString('default', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

interface IntelligentGuidedRunProps {
  run: Manifest;
  rerunGroup: manifestUtils.ManifestRerunGroup | null;
  filters: Filters;
  issues: UniversalIssueWithScreenshot[];
}

const IntelligentGuidedRun: React.FC<IntelligentGuidedRunProps> = ({
  run,
  rerunGroup,
  filters,
  issues
}) => {
  const { t } = useTranslation();
  const { id, name, created_at, updated_at, body } = run;
  const metadata = body.metadata;

  // Filter out the index step
  const steps = metadata?.progress?.filter(step => step.text) || [];
  let currentStepIndex = steps.findIndex(step => step.status === 'current');
  if (currentStepIndex === -1) {
    currentStepIndex = 0;
  }
  const totalSteps = steps.length;
  const completedSteps = metadata?.complete ? totalSteps : currentStepIndex;
  const progress = Math.trunc((completedSteps / totalSteps) * 100) || 0;

  const formattedCreationTime = created_at ? formatDateTime(created_at) : 'N/A';
  const formattedUpdateTime = updated_at ? formatDateTime(updated_at) : 'N/A';

  const duration =
    metadata?.duration && metadata.duration > 0
      ? metadata.duration / 1000 / 60
      : 0;
  const formattedDuration =
    duration < 1 ? t('less than 1 minute') : Math.round(duration) + t(' min');

  const searchParams = getSearchParamsFromFilters({
    ...getBaseFilters(filters),
    manifestId: run.id
  });

  const issuesURL = `${location.pathname}/issues?${searchParams}`;
  const runIssues = issues.filter(issue => issue.manifestId === run.id);

  return (
    <Panel>
      <PanelHeader>
        <h4>{name}</h4>
      </PanelHeader>
      <PanelContent>
        <div className={styles.testprogress__label}>
          <h5 id={`progress-${id}`}>{t('Progress')}</h5>
          <span>
            <Trans>
              <strong>{{ progress: progress }}</strong>% complete
            </Trans>
          </span>
        </div>
        <ProgressBar progress={progress} aria-labelledby={`progress-${id}`} />
        <dl>
          <dt id={`issue-found-${id}`}>
            {t('Issues found')}
            {!metadata?.complete && (
              <TooltipTabstop
                tooltip={
                  <Trans>
                    Issue found while an IGT is in Progress will not be added to
                    the total issues until the test is 100% Complete.
                  </Trans>
                }
                variant="info"
                placement="top"
              >
                <Icon type="question-circle" />
              </TooltipTabstop>
            )}
          </dt>
          <dd>
            {runIssues.length ? (
              <Link
                id={`issue-link-${id}`}
                to={issuesURL}
                aria-labelledby={`issue-link-${id} issue-found-${id}`}
              >
                {runIssues.length}
              </Link>
            ) : (
              0
            )}
          </dd>
          <dt>{t('Testing duration')}</dt>
          <dd>{formattedDuration}</dd>
          <dt>{t('Last tested on')}</dt>
          <dd>{formattedUpdateTime || formattedCreationTime}</dd>
          <dt>{t('Last tested by')}</dt>
          <dd>{metadata?.username || ''}</dd>
          {rerunGroup && (
            <>
              <dt>{t('Replay count')}</dt>
              <dd>{rerunGroup.length - 1}</dd>
            </>
          )}
        </dl>
      </PanelContent>
    </Panel>
  );
};

export default IntelligentGuidedRun;
